import type { HTMLAttributes, ReactNode } from 'react';
import { Button } from '../Button/index.js';
import { useI18nT } from '../../i18n/index.js';

export interface ErrorFallbackProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'title'
> {
  /** Short headline. Defaults to the locale's generic error label. */
  title?: ReactNode;
  /** Longer explanation. Usually derived from `error.message`. */
  description?: ReactNode;
  /** The Error instance — `error.message` is surfaced when `description` is
   *  unset. Not rendered if `description` is provided. */
  error?: Error;
  /** Retry handler; when set, a Retry button renders. */
  onRetry?: () => void;
  /** Extra controls next to the retry button (e.g. "Report issue"). */
  action?: ReactNode;
  className?: string;
}

/** Inline error state — smaller than a Dialog, larger than a Toast. Render
 *  at the scope of the failing resource (a panel, a table) so users can
 *  retry without losing surrounding UI. Spec 14 §6. */
export function ErrorFallback(props: ErrorFallbackProps): ReactNode {
  const { title, description, error, onRetry, action, className, ...rest } = props;
  const t = useI18nT();
  const classes = ['vq-error-fallback', className].filter(Boolean).join(' ');
  const resolvedTitle = title ?? t('common.empty');
  const resolvedDesc = description ?? error?.message;
  return (
    <div
      {...rest}
      className={classes}
      data-component="ErrorFallback"
      role="alert"
      aria-live="assertive"
    >
      <div className="vq-error-fallback__title">{resolvedTitle}</div>
      {resolvedDesc !== undefined ? (
        <div className="vq-error-fallback__desc">{resolvedDesc}</div>
      ) : null}
      {(onRetry !== undefined || action !== undefined) && (
        <div className="vq-error-fallback__actions">
          {onRetry !== undefined ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {t('common.retry')}
            </Button>
          ) : null}
          {action}
        </div>
      )}
    </div>
  );
}
